import { useTranslation } from '@silospot/i18n/react';
import { useQuery } from '@tanstack/react-query';
import { FileDown, Share2, TrendingUp, X } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { Btn, Card, CardHead, Kpi, KpiGrid, PageHeader } from '../../components/cld';
import { AreaChart } from '../../components/cld/charts';
import { GoogleAdsNoLinkCard, GoogleAdsNoWatchedCard } from '../../components/cld/google-ads-empty';
import { api } from '../../lib/api';
import { useSiteAdAccounts } from '../../lib/use-site-ad-accounts';
import { filterAuditToWatched, useWatchedSet } from '../../lib/watched-audit';

/** Conservative share of the wasted budget assumed recoverable once acted on. */
const RECOVERY = 0.7;

export function GoogleAdsRoiPage(): ReactElement {
  const { t } = useTranslation('pages');
  const nf = new Intl.NumberFormat('fr-FR');
  const money = (eur: number) => `${nf.format(Math.round(eur))} €`;

  const { projectId, connected, accounts, loading } = useSiteAdAccounts();
  const [picked, setPicked] = useState<string | null>(null);
  const cid = picked ?? accounts[0]?.id ?? null;
  const { watchedIds } = useWatchedSet(projectId, cid);
  const auditQ = useQuery({
    queryKey: ['gads-audit', cid],
    queryFn: () => api.googleAdsAudit(cid as string),
    enabled: !!cid,
  });
  const [share, setShare] = useState(false);

  const header = (
    <PageHeader
      title={t('gadsroi.title')}
      sub={t('gadsroi.subtitle')}
      actions={
        connected && accounts.length > 0 ? (
          <div className="flex items-center gap-2">
            <select
              value={cid ?? ''}
              onChange={(e) => setPicked(e.target.value)}
              className="rounded-[9px] border border-border-strong bg-surface px-2.5 py-1.5 text-[13px] outline-none focus:border-primary"
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name ? `${a.name} (${a.id})` : a.id}
                </option>
              ))}
            </select>
            <Btn icon={FileDown} onClick={() => window.print()}>
              {t('gadsroi.reportPdf')}
            </Btn>
            <Btn primary icon={Share2} onClick={() => setShare(true)}>
              {t('gadsroi.share')}
            </Btn>
          </div>
        ) : undefined
      }
    />
  );

  if (!connected) {
    return (
      <div>
        {header}
        <Card className="mx-auto max-w-md py-10 text-center">
          <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-accent-bg text-primary">
            <TrendingUp className="h-6 w-6" />
          </span>
          <div className="text-[16px] font-bold text-text">{t('gadsp.notConnectedTitle')}</div>
          <p className="mx-auto mt-1 max-w-xs text-[13px] text-text-2">
            {t('gadsp.notConnectedBody')}
          </p>
        </Card>
      </div>
    );
  }

  if (connected && !loading && accounts.length === 0) {
    return (
      <div>
        {header}
        <GoogleAdsNoLinkCard projectId={projectId} />
      </div>
    );
  }

  if (connected && !loading && accounts.length > 0 && watchedIds && watchedIds.size === 0) {
    return (
      <div>
        {header}
        <GoogleAdsNoWatchedCard projectId={projectId} />
      </div>
    );
  }

  const audit =
    auditQ.data && watchedIds ? filterAuditToWatched(auditQ.data, watchedIds) : auditQ.data;
  const totals = audit?.totals;
  const invalidRate =
    totals && totals.clicks + totals.invalidClicks > 0
      ? (totals.invalidClicks / (totals.clicks + totals.invalidClicks)) * 100
      : 0;
  const cpa =
    totals && totals.conversions >= 0.5 ? money(totals.costEur / totals.conversions) : '—';
  const wasted = totals?.wastedEur ?? 0;
  const savings = wasted * RECOVERY;
  const afterWasted = wasted * (1 - RECOVERY);
  const afterRate = invalidRate * (1 - RECOVERY);

  // Projection chart (clearly labelled): the rate left unchecked vs controlled.
  const months = ['M', 'M+1', 'M+2', 'M+3', 'M+4', 'M+5'];
  const without = months.map((_, i) => Math.round(invalidRate * (1 + i * 0.12) * 10) / 10);
  const withSilo = months.map(
    (_, i) =>
      Math.round(invalidRate * (1 - RECOVERY) ** (i === 0 ? 0 : 1) * (1 - i * 0.05) * 10) / 10,
  );

  return (
    <div>
      {header}

      <KpiGrid cols={4}>
        <Kpi label={t('gadsroi.kpiSpend')} value={totals ? money(totals.costEur) : '—'} />
        <Kpi label={t('gadsroi.kpiWasted')} value={totals ? money(wasted) : '—'} />
        <Kpi label={t('gadsroi.kpiCpa')} value={cpa} />
        <Kpi
          label={t('gadsroi.kpiInvalidRate')}
          value={totals ? `${nf.format(Math.round(invalidRate * 10) / 10)} %` : '—'}
        />
      </KpiGrid>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1.3fr_1fr]">
        {/* Before / after */}
        <Card>
          <CardHead title={t('gadsroi.baTitle')} />
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr>
                {[t('gadsroi.baMetric'), t('gadsroi.baBefore'), t('gadsroi.baAfter')].map(
                  (h, i) => (
                    <th
                      key={h}
                      className={`border-b border-border px-2 py-2 text-[11px] font-semibold uppercase text-text-3 ${i === 0 ? 'text-left' : 'text-right'}`}
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border-b border-[#f1f3f6] px-2 py-2.5 text-text-2">
                  {t('gadsroi.mWasted')}
                </td>
                <td className="border-b border-[#f1f3f6] px-2 py-2.5 text-right font-mono text-fraud-fg">
                  {money(wasted)}
                </td>
                <td className="border-b border-[#f1f3f6] px-2 py-2.5 text-right font-mono text-clean-fg">
                  {money(afterWasted)}
                </td>
              </tr>
              <tr>
                <td className="border-b border-[#f1f3f6] px-2 py-2.5 text-text-2">
                  {t('gadsroi.mInvalid')}
                </td>
                <td className="border-b border-[#f1f3f6] px-2 py-2.5 text-right font-mono">
                  {nf.format(Math.round(invalidRate * 10) / 10)} %
                </td>
                <td className="border-b border-[#f1f3f6] px-2 py-2.5 text-right font-mono text-clean-fg">
                  {nf.format(Math.round(afterRate * 10) / 10)} %
                </td>
              </tr>
              <tr>
                <td className="px-2 py-2.5 text-text-2">{t('gadsroi.mMonitoring')}</td>
                <td className="px-2 py-2.5 text-right text-text-3">
                  {t('gadsroi.monitoringManual')}
                </td>
                <td className="px-2 py-2.5 text-right font-semibold text-clean-fg">
                  {t('gadsroi.monitoringAuto')}
                </td>
              </tr>
            </tbody>
          </table>
          <p className="mt-3 text-[11.5px] text-text-3">{t('gadsroi.recoveryNote')}</p>
        </Card>

        {/* Cumulative savings */}
        <Card className="flex flex-col justify-center text-center">
          <CardHead title={t('gadsroi.cumTitle')} />
          <div className="py-2 text-[12px] text-text-3">{t('gadsroi.cumPotential')}</div>
          <div className="text-[34px] font-bold tracking-[-0.02em] text-clean-fg">
            {money(savings)}
          </div>
          <p className="mx-auto mt-3 max-w-xs text-[11.5px] text-text-3">{t('gadsroi.cumNote')}</p>
        </Card>
      </div>

      {/* Fraud-rate projection */}
      <Card className="mt-4">
        <CardHead title={t('gadsroi.fraudTitle')} />
        <AreaChart
          labels={months}
          series={[
            { name: t('gadsroi.fraudWithout'), color: '#dc2626', data: without },
            { name: t('gadsroi.fraudWith'), color: '#16a34a', data: withSilo },
          ]}
          height={220}
        />
      </Card>

      <div className="mt-4 inline-flex items-center gap-2 rounded-md border border-[#f6e2c4] bg-[#fdf4e7] px-[11px] py-[7px] text-[11.5px] leading-snug text-suspect-fg">
        {t('gadsroi.estimatedNote')}
      </div>

      {share ? <ShareModal onClose={() => setShare(false)} /> : null}
    </div>
  );
}

function ShareModal({ onClose }: { onClose: () => void }): ReactElement {
  const { t } = useTranslation('pages');
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center p-5">
      <button
        type="button"
        aria-label={t('gadsroi.cancel')}
        onClick={onClose}
        className="absolute inset-0 bg-[rgba(15,21,32,0.42)] backdrop-blur-[3px]"
      />
      {/* biome-ignore lint/a11y/useSemanticElements: styled modal panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('gadsroi.shareTitle')}
        className="relative w-[440px] max-w-full rounded-2xl bg-surface p-6 shadow-[0_24px_60px_rgba(16,24,40,0.3)]"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-[16px] font-bold text-text">{t('gadsroi.shareTitle')}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('gadsroi.cancel')}
            className="grid h-8 w-8 place-items-center rounded-md text-text-2 hover:bg-bg"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <Field label={t('gadsroi.shareEmail')}>
          <input type="email" placeholder="client@exemple.com" className={inputCls} />
        </Field>
        <Field label={t('gadsroi.shareExpiry')}>
          <select className={inputCls}>
            <option>{t('gadsroi.days7')}</option>
          </select>
        </Field>
        <label className="mt-1 flex items-center gap-2 text-[12.5px] text-text-2">
          <input type="checkbox" defaultChecked /> {t('gadsroi.shareLink')}
        </label>
        <div className="mt-5 flex items-center justify-between">
          <span className="text-[11.5px] text-text-3">{t('gadsroi.shareSoon')}</span>
          <Btn primary disabled>
            {t('gadsroi.shareGenerate')}
          </Btn>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  'w-full rounded-[9px] border border-border-strong px-3 py-2 text-[13px] outline-none focus:border-primary';

function Field({ label, children }: { label: string; children: React.ReactNode }): ReactElement {
  return (
    <div className="mb-3">
      <div className="mb-1 text-[12.5px] font-semibold text-text-2">{label}</div>
      {children}
    </div>
  );
}
